import React, { useState } from 'react';
import { Eye, Info, Pencil, Plane, Trash2 } from 'lucide-react';
import type { Flight } from '../../types/flight';
import { getAirportInfo } from '../../services/api/flightsApi';
import { useTranslation } from '../../useTranslation';
import { useFx } from '../../hooks/FxRatesContext';
import { flightClassLabel } from './flightFormUtils';
import { TagBadge } from '../common/TagBadge';
import { TripRouteTile } from '../common/TripRouteTile';
import { TRIP_TILE_THEMES } from '../common/tripTileThemes';
import {
  airportNameLabel,
  cityCountryLabel,
  fullAirportTitle,
} from '../../utils/airportLabels';
import {
  addDaysIso,
  arrivalDayOffset,
} from '../../utils/flightDayOffset';

const iataCodeClass =
  'text-xl sm:text-2xl font-bold tracking-tight tabular-nums bg-gradient-to-b from-slate-700 via-slate-600 to-slate-400 dark:from-slate-200 dark:via-slate-300 dark:to-slate-500 bg-clip-text text-transparent';

export const FlightCard: React.FC<{
  flight: Flight;
  selected?: boolean;
  onSelect?: () => void;
  onView?: (flight: Flight) => void;
  onEdit?: (flight: Flight) => void;
  onDelete?: (flight: Flight) => void;
}> = ({ flight, selected, onSelect, onView, onEdit, onDelete }) => {
  const [airportInfo, setAirportInfo] = useState<'dep' | 'arr' | null>(null);
  const [airportInfoContent, setAirportInfoContent] = useState('');
  const [airportInfoLoading, setAirportInfoLoading] = useState(false);

  const settings = localStorage.getItem('travelDiarySettings')
    ? JSON.parse(localStorage.getItem('travelDiarySettings')!)
    : { language: 'ru-RU', distanceUnit: 'km' };
  const { t, formatDate, formatDistance, formatNumber } = useTranslation(settings.language);
  const { toDisplay, displaySymbol } = useFx();
  const theme = TRIP_TILE_THEMES.flight;
  const useRu = settings.language.toLowerCase().startsWith('ru');
  const timeInFlightLabel = t.timeInFlight ?? 'Время в полёте';

  const depCity = cityCountryLabel(flight.departure_airport_info, flight.departureCity, useRu);
  const arrCity = cityCountryLabel(flight.arrival_airport_info, flight.arrivalCity, useRu);
  const depName = airportNameLabel(flight.departure_airport_info, flight.departureCity, useRu);
  const arrName = airportNameLabel(flight.arrival_airport_info, flight.arrivalCity, useRu);
  const depFull = fullAirportTitle(flight.departure_airport_info, flight.departureCity, useRu);
  const arrFull = fullAirportTitle(flight.arrival_airport_info, flight.arrivalCity, useRu);

  const handleAirportInfoClick = async (which: 'dep' | 'arr') => {
    if (airportInfo === which) {
      setAirportInfo(null);
      return;
    }
    setAirportInfo(which);
    setAirportInfoContent('');
    setAirportInfoLoading(true);
    const iata = which === 'dep' ? flight.departure : flight.arrival;
    const name = which === 'dep' ? flight.departureCity : flight.arrivalCity;
    const full = which === 'dep' ? depFull : arrFull;
    const unavailable = t.airportDescriptionUnavailable ?? 'Описание недоступно';
    try {
      const res = await getAirportInfo(iata, name, settings.language);
      const desc = res?.description?.trim();
      setAirportInfoContent(desc ? `${full}\n\n${desc}` : full || unavailable);
    } catch {
      setAirportInfoContent(full || unavailable);
    } finally {
      setAirportInfoLoading(false);
    }
  };

  const stop = (callback: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    callback();
  };

  const endpoint = (
    which: 'dep' | 'arr',
    city: string,
    name: string,
    iata: string,
    full: string,
    alignRight = false,
  ) => (
    <div className={alignRight ? 'text-right' : ''}>
      <div className={`inline-flex items-center gap-0.5 ${alignRight ? 'flex-row-reverse' : ''}`}>
        <span className={iataCodeClass}>
          {iata}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void handleAirportInfoClick(which);
          }}
          className="inline-flex size-5 shrink-0 items-center justify-center rounded text-stone-400 hover:text-stone-600 hover:bg-stone-100 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          title={full}
          aria-label={full}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </div>
      <p
        className={`text-sm text-stone-600 dark:text-slate-300 mt-0.5 leading-snug ${alignRight ? 'text-right' : ''}`}
        title={full}
      >
        <span className="font-medium text-stone-800 dark:text-slate-200">{city || iata}</span>
        {name && name !== city ? (
          <span className={`block text-xs text-stone-500 dark:text-slate-400 line-clamp-2 mt-0.5 ${alignRight ? 'text-right' : ''}`}>
            {name}
          </span>
        ) : null}
      </p>
    </div>
  );

  const actionBtn = 'p-1.5 rounded-lg shrink-0';
  const priceLabel =
    flight.price != null && flight.price > 0
      ? `${formatNumber(toDisplay(flight.price, 'RUB'))} ${displaySymbol}`
      : null;

  const dayOffset = arrivalDayOffset({
    departureTime: flight.departure_time,
    arrivalTime: flight.arrival_time,
    departureIata: flight.departure,
    arrivalIata: flight.arrival,
    flightDate: flight.date,
  });
  const depTime = (flight.departure_time || '').trim();
  const arrTime = (flight.arrival_time || '').trim();
  const hasTimes = Boolean(depTime || arrTime);
  const arrivalDateIso = dayOffset > 0 ? addDaysIso(flight.date, dayOffset) : null;
  const dateLabel = arrivalDateIso
    ? `${formatDate(flight.date)} → ${formatDate(arrivalDateIso)}`
    : formatDate(flight.date);

  return (
    <TripRouteTile
      selected={selected}
      onSelect={onSelect}
      theme={theme}
      language={settings.language}
      routeIcon={Plane}
      distanceLabel={formatDistance(flight.distance, settings.distanceUnit)}
      durationLabel={flight.duration ? `${timeInFlightLabel}: ${flight.duration}` : null}
      origin={endpoint('dep', depCity, depName, flight.departure, depFull)}
      destination={endpoint('arr', arrCity, arrName, flight.arrival, arrFull, true)}
      actionsDirection="column"
      meta={
        <>
          {flight.tag ? (
            <div className="flex flex-wrap items-center justify-end gap-1">
              <TagBadge tag={flight.tag} />
            </div>
          ) : null}
          <p className="text-sm font-medium text-stone-800 dark:text-slate-100 leading-snug [overflow-wrap:anywhere] break-words">
            {flight.airline} {flight.flightNumber}
          </p>
          {hasTimes ? (
            <p className="text-xs text-stone-500 dark:text-slate-400 tabular-nums">
              {depTime || '—'}
              {arrTime ? (
                <>
                  {' – '}
                  {arrTime}
                  {dayOffset > 0 ? (
                    <span className="font-bold text-stone-700 dark:text-slate-200">+{dayOffset}</span>
                  ) : null}
                </>
              ) : null}
            </p>
          ) : null}
          <p className="text-xs text-stone-500 dark:text-slate-400">{dateLabel}</p>
          {flight.class && flight.class !== 'Economy' ? (
            <div className="flex flex-wrap items-center justify-end gap-1 pt-0.5">
              <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-stone-100 text-stone-700 dark:bg-slate-700 dark:text-slate-200">
                {flightClassLabel(flight.class, t)}
              </span>
            </div>
          ) : null}
          {priceLabel ? (
            <p className={`text-base font-bold tabular-nums ${theme.price}`}>{priceLabel}</p>
          ) : null}
        </>
      }
      actions={
        <>
          {onView ? (
            <button
              type="button"
              onClick={stop(() => onView(flight))}
              className={`${actionBtn} text-stone-500 hover:bg-stone-100 dark:hover:bg-slate-700`}
              title={t.viewTrip}
            >
              <Eye className="h-4 w-4" />
            </button>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              onClick={stop(() => onEdit(flight))}
              className={`${actionBtn} text-stone-500 hover:bg-stone-100 dark:hover:bg-slate-700`}
              title={t.editFlight || t.editTrip || 'Edit'}
            >
              <Pencil className="h-4 w-4" />
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              onClick={stop(() => {
                if (window.confirm(t.confirmDeleteFlight ?? t.confirmDeleteTrip ?? 'Удалить этот рейс?')) {
                  onDelete(flight);
                }
              })}
              className={`${actionBtn} text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20`}
              title={t.deleteTrip || t.deleteFlight || 'Удалить'}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          ) : null}
        </>
      }
      extra={
        (airportInfo === 'dep' || airportInfo === 'arr') ? (
          <div className="mt-3 px-2 py-2 text-[11px] rounded-lg bg-stone-50 dark:bg-slate-700/80 text-stone-700 dark:text-slate-200 border border-stone-200 dark:border-slate-600 whitespace-pre-wrap break-words">
            {airportInfoLoading ? '…' : airportInfoContent}
          </div>
        ) : null
      }
    />
  );
};
